import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Plus, Trash2, Search, Users, X, ZoomIn, ZoomOut, Undo2, Redo2,
  Copy, AlertTriangle, Baby, Heart, Crown, GripVertical,
  Circle, RectangleHorizontal, AlignJustify, Info, Utensils,
  AlertCircle, ChevronDown, Minus, Music, Mic, Wine, Camera,
  MapPin, Coffee, Sparkles, LayoutGrid,
} from 'lucide-react';

// ═══════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════
const TABLE_TYPES = [
  { value: 'round', label: 'Rundt', Icon: Circle, defaultCap: 8 },
  { value: 'rectangular', label: 'Rectangular', Icon: RectangleHorizontal, defaultCap: 8 },
  { value: 'banquet', label: 'Long table', Icon: AlignJustify, defaultCap: 12 },
  { value: 'head', label: 'Hovedtable', Icon: Crown, defaultCap: 10 },
  { value: 'sweetheart', label: 'Love', Icon: Heart, defaultCap: 2 },
  { value: 'children', label: 'Childetable', Icon: Baby, defaultCap: 8 },
];

const RELATION_COLORS = {
  family: 'bg-amber-100 text-amber-700 border-amber-200',
  friend: 'bg-sky-100 text-sky-700 border-sky-200',
  colleague: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  other: 'bg-stone-100 text-stone-600 border-stone-200',
};
const RELATION_LABELS = { family: 'Familie', friend: 'Venn', colleague: 'Kollega', other: 'Other' };

const CANVAS_W = 2200;
const CANVAS_H = 1400;
const MAX_UNDO = 40;

const CAP_OPTIONS = [2, 4, 6, 8, 10, 12, 14, 16, 20];

// ─── Venue Element Types ───
const VENUE_TYPES = [
  { value: 'dancefloor', label: 'Dansegulv', Icon: Music, color: '#E8D5C4', defaultW: 200, defaultH: 200 },
  { value: 'stage', label: 'Scene', Icon: Mic, color: '#D4C4B0', defaultW: 220, defaultH: 100 },
  { value: 'bar', label: 'Bar', Icon: Wine, color: '#C9B99A', defaultW: 160, defaultH: 60 },
  { value: 'buffet', label: 'Buffet', Icon: Utensils, color: '#D5C8B6', defaultW: 200, defaultH: 60 },
  { value: 'entrance', label: 'Entrance', Icon: MapPin, color: '#B8C9B8', defaultW: 80, defaultH: 40 },
  { value: 'dj', label: 'DJ table', Icon: Music, color: '#C4B8D4', defaultW: 100, defaultH: 60 },
  { value: 'photobooth', label: 'Fotoboks', Icon: Camera, color: '#D4C4D4', defaultW: 120, defaultH: 100 },
  { value: 'cake', label: 'Kaketable', Icon: Coffee, color: '#E0D0C0', defaultW: 80, defaultH: 80 },
];

const VENUE_SIZES = [
  { label: 'S', factor: 0.7 },
  { label: 'M', factor: 1 },
  { label: 'L', factor: 1.4 },
  { label: 'XL', factor: 2 },
];

// ═══════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════
const uid = () => `t-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const defaultPosition = (idx) => ({
  x: 100 + (idx % 4) * 300,
  y: 100 + Math.floor(idx / 4) * 320,
});

const makeTable = (type, capacity, idx, name) => ({
  id: uid(),
  name: name || `Table ${idx + 1}`,
  type,
  capacity,
  position: defaultPosition(idx),
  seats: {},
});

const makeVenueElement = (type, existingCount) => {
  const info = VENUE_TYPES.find(v => v.value === type);
  return {
    id: `ve-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    type,
    name: info?.label || type,
    position: { x: 600 + (existingCount % 3) * 250, y: 100 + Math.floor(existingCount / 3) * 200 },
    size: { w: info?.defaultW || 120, h: info?.defaultH || 80 },
  };
};

const seatedCount = (table) => Object.values(table.seats).filter(Boolean).length;

const computeWarnings = (tables, guests) => {
  const w = [];
  const allGuests = (guests || []).filter(g => g.name?.trim() && g.rsvp !== 'declined');
  const assignedSet = new Set();
  tables.forEach(t => Object.values(t.seats).forEach(s => { if (s?.guestIndex != null) assignedSet.add(s.guestIndex); }));

  // Over-capacity
  tables.forEach(t => {
    const count = seatedCount(t);
    if (count > t.capacity) {
      w.push({ type: 'error', msg: `${t.name} has ${count} guests, but capacity of ${t.capacity}`, tableId: t.id });
    }
  });

  // Unplaced
  const unplacedCount = allGuests.length - assignedSet.size;
  if (unplacedCount > 0 && allGuests.length > 0) {
    w.push({ type: 'warning', msg: `${unplacedCount} guest${unplacedCount > 1 ? 'er' : ''} mangler tableplass` });
  }

  // Accessibility
  tables.forEach(t => {
    Object.values(t.seats).filter(Boolean).forEach(seat => {
      const g = guests?.[seat.guestIndex];
      if (g?.accessibility?.trim()) {
        w.push({ type: 'info', msg: `${g.name} has accessibility needs – check seatsing ved ${t.name}`, tableId: t.id });
      }
    });
  });

  // Children at table without any family
  tables.forEach(t => {
    if (t.type !== 'children') return;
    const seatedGuests = Object.values(t.seats).filter(Boolean).map(s => guests?.[s.guestIndex]).filter(Boolean);
    const hasParent = seatedGuests.some(g => g.hasChildren);
    const hasChild = seatedGuests.length > 0;
    if (hasChild && !hasParent) {
      w.push({ type: 'info', msg: `${t.name} has guests without registered guardians nearby`, tableId: t.id });
    }
  });

  return w;
};

// ═══════════════════════════════════════════════════════
// GUEST TOOLTIP (on hover over seated guest)
// ═══════════════════════════════════════════════════════
const GuestTooltip = ({ guest, style }) => {
  if (!guest) return null;
  const tags = [];
  if (guest.relation) tags.push(RELATION_LABELS[guest.relation] || guest.relation);
  if (guest.meal && guest.meal !== 'standard') tags.push(guest.meal);
  if (guest.accessibility?.trim()) tags.push('♿ ' + guest.accessibility);
  if (guest.allergies?.trim()) tags.push('Allrgy: ' + guest.allergies);
  if (guest.hasChildren) tags.push(`Child: ${guest.childrenCount || '?'}`);
  if (guest.plusOne) tags.push('+1' + (guest.plusOneName ? `: ${guest.plusOneName}` : ''));
  if (guest.notes?.trim()) tags.push(guest.notes);

  return (
    <div style={style} className="absolute z-50 bg-white rounded-xl shadow-lg border border-stone-200 p-3 min-w-[180px] pointer-events-none animate-fade-in-up">
      <p className="text-xs font-sans font-bold text-stone-800 mb-1">{guest.name}</p>
      {tags.length > 0 && (
        <div className="space-y-0.5">
          {tags.map((t, i) => (
            <p key={i} className="text-[10px] font-sans text-stone-500 leading-tight">{t}</p>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════
// SEAT SLOT (drop zone for a single seat)
// ═══════════════════════════════════════════════════════
const SeatSlot = ({ seatIdx, tableId, guest, guestData, style, className, onDrop, onRemove, isActive }) => {
  const [over, setOver] = useState(false);
  const [hovered, setHovered] = useState(false);

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setOver(true); };
  const handleDragLeave = () => setOver(false);
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setOver(false);
    const idx = parseInt(e.dataTransfer.getData('guestIndex'));
    if (!isNaN(idx)) onDrop(tableId, seatIdx, idx);
  };

  // Make seated guests draggable to move between seats/tables
  const handleDragStart = (e) => {
    if (!guest) return;
    e.dataTransfer.setData('guestIndex', guest.guestIndex.toString());
    e.stopPropagation();
  };

  const hasAccessibility = guestData?.accessibility?.trim();
  const hasAllrgy = guestData?.allergies?.trim();

  return (
    <div style={style}
      className={`relative select-none ${className || ''}`}
      onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      draggable={!!guest}
      onDragStart={handleDragStart}
    >
      <div className={`w-full h-full rounded-full flex items-center justify-center transition-all
        ${guest
          ? 'bg-primary/10 border-2 border-primary/30 hover:border-primary/60 cursor-grab active:cursor-grabbing'
          : over
            ? 'bg-primary/20 border-2 border-primary border-dashed scale-110'
            : isActive
              ? 'bg-stone-50 border-2 border-stone-300 border-dashed'
              : 'bg-white/80 border border-stone-200 hover:border-stone-300'
        } cursor-pointer`}
        onClick={(e) => { e.stopPropagation(); if (guest) onRemove(tableId, seatIdx); }}
      >
        {guest ? (
          <div className="flex flex-col items-center gap-0 px-0.5 overflow-hidden">
            <span className="text-[8px] font-sans font-semibold text-primary truncate max-w-full leading-tight">
              {guest.name?.split(' ')[0] || '?'}
            </span>
            <div className="flex items-center gap-0.5 mt-0.5">
              {hasAccessibility && <span className="text-[7px]">{'♿'}</span>}
              {hasAllrgy && <Utensils className="w-2 h-2 text-amber-500" />}
              <X className="w-2 h-2 text-stone-300 hover:text-red-400" />
            </div>
          </div>
        ) : (
          <div className="w-1.5 h-1.5 rounded-full bg-stone-200" />
        )}
      </div>
      {/* Tooltip */}
      {hovered && guest && guestData && (
        <GuestTooltip guest={guestData} style={{ bottom: '110%', left: '50%', transform: 'translateX(-50%)' }} />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════
// TABLE VISUALIZATIONS
// ═══════════════════════════════════════════════════════
const RoundSeats = ({ table, guests, onDrop, onRemove, isActive }) => {
  const n = table.capacity;
  const r = Math.max(48, 10 * n);
  const seatSize = 44;
  const center = r + seatSize / 2 + 6;
  const total = center * 2;

  return (
    <div className="relative mx-auto" style={{ width: total, height: total }}>
      <div className="absolute rounded-full bg-[#F7F5F2] border-2 border-stone-200/80 flex items-center justify-center"
        style={{ width: r * 1.15, height: r * 1.15, top: center - r * 0.575, left: center - r * 0.575 }}>
        <span className="text-[9px] font-sans font-bold text-stone-400 uppercase tracking-wider text-center px-1 leading-tight">{table.name}</span>
      </div>
      {Array.from({ length: n }, (_, i) => {
        const a = (i / n) * 2 * Math.PI - Math.PI / 2;
        const x = center + r * Math.cos(a) - seatSize / 2;
        const y = center + r * Math.sin(a) - seatSize / 2;
        const seat = table.seats[i];
        return (
          <SeatSlot key={i} seatIdx={i} tableId={table.id} guest={seat}
            guestData={seat ? guests?.[seat.guestIndex] : null}
            style={{ position: 'absolute', left: x, top: y, width: seatSize, height: seatSize }}
            onDrop={onDrop} onRemove={onRemove} isActive={isActive} />
        );
      })}
    </div>
  );
};

const LinearSeats = ({ table, guests, onDrop, onRemove, isActive, singleSide }) => {
  const n = table.capacity;
  const topCount = singleSide ? n : Math.ceil(n / 2);
  const bottomCount = singleSide ? 0 : n - topCount;
  const seatW = 52;
  const seatH = 34;

  const renderRow = (seats, startIdx) => (
    <div className="flex items-center justify-center gap-1">
      {seats.map((_, i) => {
        const idx = startIdx + i;
        const seat = table.seats[idx];
        return (
          <SeatSlot key={idx} seatIdx={idx} tableId={table.id} guest={seat}
            guestData={seat ? guests?.[seat.guestIndex] : null}
            style={{ width: seatW, height: seatH }}
            className="rounded-lg"
            onDrop={onDrop} onRemove={onRemove} isActive={isActive} />
        );
      })}
    </div>
  );

  return (
    <div className="space-y-1 mx-auto" style={{ maxWidth: Math.max(topCount, bottomCount) * (seatW + 4) + 40 }}>
      {renderRow(Array(topCount), 0)}
      <div className="mx-2 bg-[#F7F5F2] border-2 border-stone-200/80 rounded-lg py-2 px-4 text-center">
        <span className="text-[9px] font-sans font-bold text-stone-400 uppercase tracking-wider">{table.name}</span>
      </div>
      {bottomCount > 0 && renderRow(Array(bottomCount), topCount)}
    </div>
  );
};

const SweetheartSeats = ({ table, guests, onDrop, onRemove, isActive }) => (
  <div className="flex flex-col items-center gap-2">
    <div className="flex items-center gap-3">
      {[0, 1].map(i => {
        const seat = table.seats[i];
        return (
          <SeatSlot key={i} seatIdx={i} tableId={table.id} guest={seat}
            guestData={seat ? guests?.[seat.guestIndex] : null}
            style={{ width: 52, height: 52 }}
            onDrop={onDrop} onRemove={onRemove} isActive={isActive} />
        );
      })}
    </div>
    <div className="w-24 h-12 rounded-xl bg-[#F7F5F2] border-2 border-stone-200/80 flex items-center justify-center">
      <Heart className="w-3 h-3 text-primary/40 mr-1" />
      <span className="text-[8px] font-sans font-bold text-stone-400 uppercase tracking-wider">{table.name}</span>
    </div>
  </div>
);

const renderTableViz = (table, guests, onDrop, onRemove, isActive) => {
  switch (table.type) {
    case 'round':
    case 'children':
      return <RoundSeats table={table} guests={guests} onDrop={onDrop} onRemove={onRemove} isActive={isActive} />;
    case 'rectangular':
      return <LinearSeats table={table} guests={guests} onDrop={onDrop} onRemove={onRemove} isActive={isActive} />;
    case 'banquet':
      return <LinearSeats table={table} guests={guests} onDrop={onDrop} onRemove={onRemove} isActive={isActive} />;
    case 'head':
      return <LinearSeats table={table} guests={guests} onDrop={onDrop} onRemove={onRemove} isActive={isActive} singleSide />;
    case 'sweetheart':
      return <SweetheartSeats table={table} guests={guests} onDrop={onDrop} onRemove={onRemove} isActive={isActive} />;
    default:
      return <RoundSeats table={table} guests={guests} onDrop={onDrop} onRemove={onRemove} isActive={isActive} />;
  }
};

// ═══════════════════════════════════════════════════════
// TABLE CARD (positioned on canvas)
// ═══════════════════════════════════════════════════════
const TableCard = ({ table, guests, onDrop, onRemove, onUpdate, onDuplicate, onDelete, onDragStart, isActive, isSelected, onSelect }) => {
  const [editing, setEditing] = useState(false);
  const typeInfo = TABLE_TYPES.find(t => t.value === table.type);
  const count = seatedCount(table);
  const isFull = count >= table.capacity;
  const isOver = count > table.capacity;

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    onSelect(table.id);
    onDragStart(table.id, e);
  };

  return (
    <div data-testid={`table-${table.id}`}
      className={`relative bg-white/95 backdrop-blur-sm rounded-2xl border-2 transition-shadow ${
        isSelected ? 'border-primary/50 shadow-lg' : isOver ? 'border-red-300 shadow-md' : 'border-stone-200/80 shadow-sm hover:shadow-md'
      }`}
      onClick={(e) => { e.stopPropagation(); onSelect(table.id); }}
    >
      {/* Drag handle + header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-stone-100 cursor-grab active:cursor-grabbing"
        onMouseDown={handleMouseDown}
        data-testid={`table-drag-${table.id}`}
      >
        <GripVertical className="w-3 h-3 text-stone-300 flex-shrink-0" />
        {typeInfo && <typeInfo.Icon className="w-3 h-3 text-stone-400 flex-shrink-0" />}
        {editing ? (
          <input value={table.name} autoFocus
            onChange={e => onUpdate(table.id, { name: e.target.value })}
            onBlur={() => setEditing(false)}
            onKeyDown={e => { if (e.key === 'Enter') setEditing(false); }}
            className="text-xs font-sans font-bold text-stone-700 bg-transparent border-b border-primary focus:outline-none w-20"
            onClick={e => e.stopPropagation()} />
        ) : (
          <span className="text-[10px] font-sans font-bold text-stone-600 truncate cursor-text"
            onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }}>{table.name}</span>
        )}
        <span className={`text-[10px] font-sans font-bold ml-auto px-1.5 py-0.5 rounded-full ${
          isOver ? 'bg-red-100 text-red-600' : isFull ? 'bg-green-100 text-green-600' : 'bg-stone-100 text-stone-500'
        }`}>{count}/{table.capacity}</span>
      </div>

      {/* Edit controls (when selected) */}
      {isSelected && (
        <div className="flex items-center gap-1 px-3 py-1.5 border-b border-stone-100 bg-stone-50/50 relative z-10">
          <label className="text-[9px] font-sans text-stone-400 mr-1">Seter:</label>
          <select value={table.capacity} onChange={e => onUpdate(table.id, { capacity: parseInt(e.target.value) })}
            className="text-[10px] font-sans border border-stone-200 rounded-lg px-1.5 py-0.5 bg-white focus:border-primary focus:outline-none"
            onClick={e => e.stopPropagation()}>
            {CAP_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <select value={table.type} onChange={e => onUpdate(table.id, { type: e.target.value })}
            className="text-[10px] font-sans border border-stone-200 rounded-lg px-1.5 py-0.5 bg-white focus:border-primary focus:outline-none ml-1"
            onClick={e => e.stopPropagation()}>
            {TABLE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <div className="flex items-center gap-0.5 ml-auto">
            <button onClick={(e) => { e.stopPropagation(); onDuplicate(table.id); }} className="p-1 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600 transition-colors" title="Dupliser">
              <Copy className="w-3 h-3" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); onDelete(table.id); }} className="p-1 rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors" title="Delete">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Table visualization */}
      <div className="p-3">
        {renderTableViz(table, guests, onDrop, onRemove, isActive)}
      </div>

      {/* Type badge */}
      {(table.type === 'head' || table.type === 'sweetheart' || table.type === 'children') && (
        <div className="absolute -top-2 -right-2">
          <div className={`w-5 h-5 rounded-full flex items-center justify-center shadow-sm border border-white ${
            table.type === 'head' ? 'bg-amber-100' : table.type === 'sweetheart' ? 'bg-rose-100' : 'bg-sky-100'
          }`}>
            {table.type === 'head' && <Crown className="w-2.5 h-2.5 text-amber-600" />}
            {table.type === 'sweetheart' && <Heart className="w-2.5 h-2.5 text-rose-500" />}
            {table.type === 'children' && <Baby className="w-2.5 h-2.5 text-sky-600" />}
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════
// GUEST CHIP (draggable, in sidebar)
// ═══════════════════════════════════════════════════════
const GuestChip = ({ guest, index, isDragging, onDragStart, onDragEnd }) => {
  const tagColor = RELATION_COLORS[guest.relation] || RELATION_COLORS.other;
  const hasNeeds = guest.accessibility?.trim();
  const hasAllrgy = guest.allergies?.trim();

  return (
    <div draggable data-testid={`drag-guest-${index}`}
      onDragStart={(e) => { e.dataTransfer.setData('guestIndex', index.toString()); onDragStart(index); }}
      onDragEnd={onDragEnd}
      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-xl border cursor-grab active:cursor-grabbing transition-all text-left
        ${isDragging ? 'opacity-30 scale-95' : 'bg-white border-stone-200 hover:border-primary/40 hover:shadow-sm'}`}>
      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[9px] font-sans font-bold text-primary flex-shrink-0">
        {guest.name?.[0]?.toUpperCase() || '?'}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-sans font-medium text-stone-700 truncate leading-tight">{guest.name}</p>
        <div className="flex items-center gap-1 mt-0.5">
          {guest.relation && (
            <span className={`text-[8px] font-sans font-semibold px-1.5 py-0 rounded-full border ${tagColor}`}>
              {RELATION_LABELS[guest.relation] || guest.relation}
            </span>
          )}
          {hasNeeds && <span className="text-[8px]" title={guest.accessibility}>{'♿'}</span>}
          {hasAllrgy && <Utensils className="w-2.5 h-2.5 text-amber-500" title={guest.allergies} />}
          {guest.hasChildren && <Baby className="w-2.5 h-2.5 text-sky-500" />}
          {guest.plusOne && <span className="text-[8px] font-sans font-bold text-stone-400">+1</span>}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════
// WARNINGS PANEL
// ═══════════════════════════════════════════════════════
const WarningsPanel = ({ warnings }) => {
  const [open, setOpen] = useState(true);
  if (!warnings.length) return null;

  const iconMap = { error: AlertTriangle, warning: AlertTriangle, info: Info };
  const colorMap = {
    error: 'text-red-600 bg-red-50 border-red-200',
    warning: 'text-amber-600 bg-amber-50 border-amber-200',
    info: 'text-stone-500 bg-stone-50 border-stone-200',
  };

  return (
    <div data-testid="seating-warnings">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 w-full text-left mb-1.5">
        <AlertTriangle className="w-3 h-3 text-amber-600" />
        <span className="text-[10px] font-sans font-bold text-stone-600 uppercase tracking-wide">Varsler</span>
        <span className="text-[9px] font-sans font-bold bg-amber-100 text-amber-700 px-1.5 rounded-full">{warnings.length}</span>
        <ChevronDown className={`w-3 h-3 text-stone-400 ml-auto transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="space-y-1">
          {warnings.map((w, i) => {
            const Icon = iconMap[w.type] || Info;
            const colors = colorMap[w.type] || colorMap.info;
            return (
              <div key={i} className={`flex items-start gap-2 rounded-lg border px-2.5 py-1.5 ${colors}`}>
                <Icon className="w-3 h-3 flex-shrink-0 mt-0.5" />
                <p className="text-[10px] font-sans leading-snug">{w.msg}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════
// VENUE ELEMENT CARD (on canvas)
// ═══════════════════════════════════════════════════════
const VenueElementCard = ({ el, isSelected, onSelect, onDragStart, onUpdate, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const info = VENUE_TYPES.find(v => v.value === el.type);
  const Icon = info?.Icon || MapPin;
  const bgColor = info?.color || '#ddd';

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    onSelect(el.id);
    onDragStart(el.id, e);
  };

  return (
    <div data-testid={`venue-${el.id}`}
      className={`rounded-xl border-2 transition-shadow cursor-grab active:cursor-grabbing select-none ${
        isSelected ? 'border-primary/60 shadow-lg ring-2 ring-primary/20' : 'border-stone-300/60 shadow-sm hover:shadow-md'
      }`}
      style={{ width: el.size.w, height: el.size.h, background: bgColor }}
      onMouseDown={handleMouseDown}
      onClick={(e) => { e.stopPropagation(); onSelect(el.id); }}
    >
      <div className="flex flex-col items-center justify-center h-full p-1 relative">
        <Icon className="w-4 h-4 text-stone-600/60 mb-0.5" />
        {editing ? (
          <input value={el.name} autoFocus
            onChange={e => onUpdate(el.id, { name: e.target.value })}
            onBlur={() => setEditing(false)}
            onKeyDown={e => { if (e.key === 'Enter') setEditing(false); }}
            className="text-[9px] font-sans font-bold text-stone-700 bg-white/60 rounded px-1 text-center w-full focus:outline-none"
            onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()} />
        ) : (
          <span className="text-[9px] font-sans font-bold text-stone-600 uppercase tracking-wider text-center leading-tight cursor-text"
            onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }}>{el.name}</span>
        )}
        {isSelected && (
          <div className="absolute -top-2 -right-2 flex gap-0.5">
            {VENUE_SIZES.map(s => (
              <button key={s.label} onMouseDown={e => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); const base = VENUE_TYPES.find(v => v.value === el.type); onUpdate(el.id, { size: { w: Math.round((base?.defaultW || 120) * s.factor), h: Math.round((base?.defaultH || 80) * s.factor) } }); }}
                className={`w-4 h-4 rounded text-[7px] font-sans font-bold flex items-center justify-center border transition-colors ${
                  Math.abs(el.size.w - (info?.defaultW || 120) * s.factor) < 5 ? 'bg-primary text-white border-primary' : 'bg-white text-stone-500 border-stone-300 hover:border-primary'
                }`}>{s.label}</button>
            ))}
            <button onMouseDown={e => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onDelete(el.id); }}
              className="w-4 h-4 rounded bg-white text-red-400 border border-stone-300 hover:bg-red-50 hover:text-red-600 flex items-center justify-center transition-colors">
              <X className="w-2.5 h-2.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════
export const SeatingChart = ({ seating, onChange, guests, couple }) => {
  // ─── UI State ───
  const [zoom, setZoom] = useState(0.75);
  const [search, setSearch] = useState('');
  const [filterTag, setFilterTag] = useState(null);
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [newTableType, setNewTableType] = useState('round');
  const [dragGuestIdx, setDragGuestIdx] = useState(null);
  const [showVenueMenu, setShowVenueMenu] = useState(false);

  // ─── Canvas drag state (tables + venue) ───
  const [draggingTableId, setDraggingTableId] = useState(null);
  const [draggingVenueId, setDraggingVenueId] = useState(null);
  const [selectedVenueId, setSelectedVenueId] = useState(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const canvasRef = useRef(null);
  const innerRef = useRef(null);

  // ─── Undo / Redo ───
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const skipHistoryRef = useRef(false);

  const tables = seating?.tables || [];
  const venueElements = seating?.venueElements || [];

  // ─── Commit change with history ───
  const commitChange = useCallback((newTables, newVenue) => {
    if (!skipHistoryRef.current) {
      setUndoStack(prev => [...prev.slice(-(MAX_UNDO - 1)), JSON.stringify({ t: tables, v: venueElements })]);
      setRedoStack([]);
    }
    skipHistoryRef.current = false;
    onChange({ ...(seating || {}), tables: newTables, venueElements: newVenue });
  }, [tables, venueElements, seating, onChange]);

  const updateTables = useCallback((newTables) => commitChange(newTables, venueElements), [commitChange, venueElements]);
  const updateVenue = useCallback((newVenue) => commitChange(tables, newVenue), [commitChange, tables]);

  const undo = useCallback(() => {
    if (undoStack.length === 0) return;
    const current = JSON.stringify({ t: tables, v: venueElements });
    setRedoStack(prev => [...prev, current]);
    const prev = JSON.parse(undoStack[undoStack.length - 1]);
    setUndoStack(s => s.slice(0, -1));
    skipHistoryRef.current = true;
    onChange({ ...(seating || {}), tables: prev.t || prev, venueElements: prev.v || [] });
  }, [undoStack, tables, venueElements, seating, onChange]);

  const redo = useCallback(() => {
    if (redoStack.length === 0) return;
    const current = JSON.stringify({ t: tables, v: venueElements });
    setUndoStack(prev => [...prev, current]);
    const next = JSON.parse(redoStack[redoStack.length - 1]);
    setRedoStack(s => s.slice(0, -1));
    skipHistoryRef.current = true;
    onChange({ ...(seating || {}), tables: next.t || next, venueElements: next.v || [] });
  }, [redoStack, tables, venueElements, seating, onChange]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) { e.preventDefault(); redo(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo]);

  // ─── Computed ───
  const assignedIndices = useMemo(() => {
    const set = new Set();
    tables.forEach(t => Object.values(t.seats).forEach(s => { if (s?.guestIndex != null) set.add(s.guestIndex); }));
    return set;
  }, [tables]);

  const validGuests = useMemo(() =>
    (guests || []).map((g, i) => ({ ...g, _idx: i })).filter(g => g.name?.trim() && g.rsvp !== 'declined'),
    [guests]
  );

  const unassigned = useMemo(() => validGuests.filter(g => !assignedIndices.has(g._idx)), [validGuests, assignedIndices]);

  const filteredUnassigned = useMemo(() => {
    let list = unassigned;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(g => g.name?.toLowerCase().includes(q));
    }
    if (filterTag) {
      list = list.filter(g => g.relation === filterTag);
    }
    return list;
  }, [unassigned, search, filterTag]);

  const warnings = useMemo(() => computeWarnings(tables, guests), [tables, guests]);

  const totalSeats = tables.reduce((s, t) => s + t.capacity, 0);
  const totalAssigned = assignedIndices.size;

  // ─── Table CRUD ───
  const addTable = () => {
    const typeInfo = TABLE_TYPES.find(t => t.value === newTableType);
    const cap = typeInfo?.defaultCap || 8;
    updateTables([...tables, makeTable(newTableType, cap, tables.length)]);
  };

  const removeTable = (id) => updateTables(tables.filter(t => t.id !== id));

  const updateTable = (id, updates) => {
    updateTables(tables.map(t => {
      if (t.id !== id) return t;
      const updated = { ...t, ...updates };
      // If capacity reduced, remove excess guests
      if (updates.capacity && updates.capacity < t.capacity) {
        const newSeats = { ...updated.seats };
        Object.keys(newSeats).forEach(k => {
          if (parseInt(k) >= updates.capacity) delete newSeats[k];
        });
        updated.seats = newSeats;
      }
      return updated;
    }));
  };

  const duplicateTable = (id) => {
    const original = tables.find(t => t.id === id);
    if (!original) return;
    const dup = {
      ...original,
      id: uid(),
      name: original.name + ' (kopi)',
      seats: {},
      position: { x: original.position.x + 40, y: original.position.y + 40 },
    };
    updateTables([...tables, dup]);
  };

  // ─── Guest assignment ───
  const dropGuest = useCallback((tableId, seatIdx, guestIdx) => {
    const cleaned = tables.map(t => {
      const ns = { ...t.seats };
      Object.keys(ns).forEach(k => { if (ns[k]?.guestIndex === guestIdx) delete ns[k]; });
      return { ...t, seats: ns };
    });
    const g = guests?.[guestIdx];
    const placed = cleaned.map(t => t.id === tableId
      ? { ...t, seats: { ...t.seats, [seatIdx]: { guestIndex: guestIdx, name: g?.name || `Guest ${guestIdx + 1}` } } }
      : t
    );
    updateTables(placed);
    setDragGuestIdx(null);
  }, [tables, guests, updateTables]);

  const removeGuest = useCallback((tableId, seatIdx) => {
    updateTables(tables.map(t => t.id === tableId
      ? { ...t, seats: (() => { const s = { ...t.seats }; delete s[seatIdx]; return s; })() }
      : t
    ));
  }, [tables, updateTables]);

  const autoAssign = () => {
    let remaining = [...unassigned];
    const newTables = tables.map(t => {
      const ns = { ...t.seats };
      for (let i = 0; i < t.capacity && remaining.length > 0; i++) {
        if (!ns[i]) {
          const g = remaining.shift();
          ns[i] = { guestIndex: g._idx, name: g.name };
        }
      }
      return { ...t, seats: ns };
    });
    updateTables(newTables);
  };

  // ─── Venue Element CRUD ───
  const addVenueElement = (type) => {
    updateVenue([...venueElements, makeVenueElement(type, venueElements.length)]);
    setShowVenueMenu(false);
  };
  const removeVenueElement = (id) => updateVenue(venueElements.filter(v => v.id !== id));
  const updateVenueElement = (id, updates) => updateVenue(venueElements.map(v => v.id === id ? { ...v, ...updates } : v));

  // ─── Canvas: Table dragging ───
  const screenToCanvas = useCallback((clientX, clientY) => {
    const outerRect = canvasRef.current?.getBoundingClientRect();
    if (!outerRect) return { x: 0, y: 0 };
    const scrollLeft = canvasRef.current.scrollLeft;
    const scrollTop = canvasRef.current.scrollTop;
    return {
      x: (clientX - outerRect.left + scrollLeft) / zoom,
      y: (clientY - outerRect.top + scrollTop) / zoom,
    };
  }, [zoom]);

  const handleTableDragStart = useCallback((tableId, e) => {
    const table = tables.find(t => t.id === tableId);
    if (!table) return;
    const canvasPos = screenToCanvas(e.clientX, e.clientY);
    dragOffsetRef.current = {
      x: canvasPos.x - table.position.x,
      y: canvasPos.y - table.position.y,
    };
    setDraggingTableId(tableId);
  }, [tables, screenToCanvas]);

  const handleVenueDragStart = useCallback((venueId, e) => {
    const el = venueElements.find(v => v.id === venueId);
    if (!el) return;
    const canvasPos = screenToCanvas(e.clientX, e.clientY);
    dragOffsetRef.current = {
      x: canvasPos.x - el.position.x,
      y: canvasPos.y - el.position.y,
    };
    setDraggingVenueId(venueId);
  }, [venueElements, screenToCanvas]);

  // Unified drag handler for tables and venue elements
  const activeDragId = draggingTableId || draggingVenueId;
  const isDraggingVenue = !!draggingVenueId;

  useEffect(() => {
    if (!activeDragId) return;

    const handleMove = (e) => {
      const pos = screenToCanvas(e.clientX, e.clientY);
      const x = Math.max(0, Math.min(CANVAS_W - 50, pos.x - dragOffsetRef.current.x));
      const y = Math.max(0, Math.min(CANVAS_H - 50, pos.y - dragOffsetRef.current.y));
      const selector = isDraggingVenue ? `[data-venue-pos="${activeDragId}"]` : `[data-table-pos="${activeDragId}"]`;
      const el = document.querySelector(selector);
      if (el) {
        el.style.left = `${x}px`;
        el.style.top = `${y}px`;
        el._pos = { x, y };
      }
    };

    const handleUp = () => {
      const selector = isDraggingVenue ? `[data-venue-pos="${activeDragId}"]` : `[data-table-pos="${activeDragId}"]`;
      const el = document.querySelector(selector);
      if (el?._pos) {
        if (isDraggingVenue) {
          updateVenue(venueElements.map(v => v.id === activeDragId ? { ...v, position: el._pos } : v));
        } else {
          updateTables(tables.map(t => t.id === activeDragId ? { ...t, position: el._pos } : t));
        }
        delete el._pos;
      }
      setDraggingTableId(null);
      setDraggingVenueId(null);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [activeDragId, isDraggingVenue, zoom, tables, venueElements, updateTables, updateVenue, screenToCanvas]);

  // ─── Zoom via wheel ───
  const handleWheel = useCallback((e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      setZoom(z => Math.max(0.3, Math.min(2, z - e.deltaY * 0.002)));
    }
  }, []);

  // ─── Deselect on canvas click ───
  const handleCanvasClick = () => { setSelectedTableId(null); setSelectedVenueId(null); };

  // ═══ RENDER ═══
  return (
    <div data-testid="seating-chart" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-sans font-semibold text-stone-800 text-sm">Seating chart</h3>
          <p className="text-xs font-sans text-stone-500 mt-0.5">Drag guests to seats &bull; Drag tables to move them</p>
          <p className="text-[10px] font-sans text-stone-400 mt-1 leading-relaxed">
            Add tables from the toolbar, drag guests from the side panel to available seats. Use Ctrl+Z to undo. Guests from the guest list appear automatically.
          </p>
        </div>
        <div className="flex items-center gap-3 text-[10px] font-sans">
          <span className="text-stone-500"><span className="font-bold text-primary">{totalAssigned}</span> / {validGuests.length} seatst</span>
          <span className="text-stone-400">{tables.length} tables &bull; {totalSeats} seats{venueElements.length > 0 && ` &bull; ${venueElements.length} elements`}</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 bg-white rounded-xl border border-stone-200/80 p-2.5 flex-wrap" data-testid="seating-toolbar">
        {/* Table type selector */}
        <div className="flex gap-0.5">
          {TABLE_TYPES.map(t => (
            <button key={t.value} onClick={() => setNewTableType(t.value)}
              data-testid={`table-type-${t.value}`}
              className={`flex items-center gap-1 text-[10px] font-sans px-2 py-1.5 rounded-lg border transition-all ${
                newTableType === t.value ? 'bg-primary/10 border-primary text-primary font-semibold' : 'bg-white border-stone-200 text-stone-500 hover:border-stone-300'
              }`}>
              <t.Icon className="w-3 h-3" />
              <span className="hidden sm:inline">{t.label}</span>
            </button>
          ))}
        </div>

        <button data-testid="add-table-btn" onClick={addTable}
          className="flex items-center gap-1 text-[10px] font-sans font-semibold text-white bg-primary hover:bg-primary/90 px-3 py-1.5 rounded-lg transition-colors">
          <Plus className="w-3 h-3" /> Table
        </button>

        {/* Venue element dropdown */}
        <div className="relative">
          <button data-testid="venue-menu-btn" onClick={() => setShowVenueMenu(!showVenueMenu)}
            className="flex items-center gap-1 text-[10px] font-sans font-semibold text-stone-600 bg-stone-100 hover:bg-stone-200 px-3 py-1.5 rounded-lg border border-stone-200 transition-colors">
            <LayoutGrid className="w-3 h-3" /> Venue <ChevronDown className={`w-2.5 h-2.5 transition-transform ${showVenueMenu ? 'rotate-180' : ''}`} />
          </button>
          {showVenueMenu && (
            <div className="absolute top-full left-0 mt-1 bg-white rounded-xl border border-stone-200 shadow-lg py-1 z-50 min-w-[140px]" data-testid="venue-menu-dropdown">
              {VENUE_TYPES.map(v => (
                <button key={v.value} data-testid={`add-venue-${v.value}`}
                  onClick={() => addVenueElement(v.value)}
                  className="flex items-center gap-2 w-full px-3 py-1.5 text-[10px] font-sans text-stone-600 hover:bg-stone-50 text-left transition-colors">
                  <div className="w-4 h-3 rounded" style={{ background: v.color }} />
                  {v.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="w-px h-5 bg-stone-200 mx-1" />

        {/* Undo / Redo */}
        <button onClick={undo} disabled={undoStack.length === 0} data-testid="undo-btn"
          className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="Angre (Ctrl+Z)">
          <Undo2 className="w-3.5 h-3.5" />
        </button>
        <button onClick={redo} disabled={redoStack.length === 0} data-testid="redo-btn"
          className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="Redo (Ctrl+Shift+Z)">
          <Redo2 className="w-3.5 h-3.5" />
        </button>

        <div className="w-px h-5 bg-stone-200 mx-1" />

        {/* Zoom */}
        <button onClick={() => setZoom(z => Math.max(0.3, z - 0.1))} data-testid="zoom-out-btn"
          className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600 transition-colors" title="Zoom ut">
          <ZoomOut className="w-3.5 h-3.5" />
        </button>
        <span className="text-[10px] font-sans font-bold text-stone-500 w-10 text-center">{Math.round(zoom * 100)}%</span>
        <button onClick={() => setZoom(z => Math.min(2, z + 0.1))} data-testid="zoom-in-btn"
          className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600 transition-colors" title="Zoom inn">
          <ZoomIn className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => setZoom(0.75)} className="text-[9px] font-sans text-stone-400 hover:text-stone-600 px-1.5 py-0.5 rounded transition-colors">
          Tilpass
        </button>
      </div>

      {/* Main area: Canvas + Sidebar */}
      <div className="flex gap-4" style={{ minHeight: 520 }}>

        {/* ─── Canvas ─── */}
        <div className="flex-1 rounded-2xl border border-stone-200/80 bg-[#FAFAF8] overflow-auto relative"
          ref={canvasRef}
          style={{ maxHeight: 600 }}
          onWheel={handleWheel}
          onClick={handleCanvasClick}
          data-testid="seating-canvas"
        >
          {tables.length === 0 && venueElements.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full min-h-[500px] text-center p-8">
              <div className="w-16 h-16 rounded-2xl bg-stone-100 flex items-center justify-center mb-4">
                <Users className="w-7 h-7 text-stone-300" />
              </div>
              <p className="text-sm font-sans font-medium text-stone-400 mb-1">None tables lagt til yet</p>
              <p className="text-xs font-sans text-stone-300 max-w-xs">Add tables or venue elements from the toolbar to get started</p>
            </div>
          ) : (
            /* Scrollable area sized to zoom level */
            <div style={{ width: CANVAS_W * zoom, height: CANVAS_H * zoom, position: 'relative' }}>
              {/* Canvas content (scaled) */}
              <div style={{
                transform: `scale(${zoom})`,
                transformOrigin: '0 0',
                width: CANVAS_W,
                height: CANVAS_H,
                position: 'absolute',
                top: 0, left: 0,
                backgroundImage: 'radial-gradient(circle, #d8d4ce 0.8px, transparent 0.8px)',
                backgroundSize: '32px 32px',
              }}>
                {/* Venue elements (rendered behind tables) */}
                {venueElements.map(ve => (
                  <div key={ve.id}
                    data-venue-pos={ve.id}
                    style={{
                      position: 'absolute',
                      left: ve.position.x,
                      top: ve.position.y,
                      transform: `scale(${1 / zoom})`,
                      transformOrigin: '0 0',
                      zIndex: 1,
                    }}
                  >
                    <VenueElementCard
                      el={ve}
                      isSelected={selectedVenueId === ve.id}
                      onSelect={(id) => { setSelectedVenueId(id); setSelectedTableId(null); }}
                      onDragStart={handleVenueDragStart}
                      onUpdate={updateVenueElement}
                      onDelete={removeVenueElement}
                    />
                  </div>
                ))}
                {/* Tables */}
                {tables.map(table => (
                  <div key={table.id}
                    data-table-pos={table.id}
                    style={{
                      position: 'absolute',
                      left: table.position.x,
                      top: table.position.y,
                      transform: `scale(${1 / zoom})`,
                      transformOrigin: '0 0',
                      zIndex: 2,
                    }}
                  >
                    <TableCard
                      table={table}
                      guests={guests}
                      onDrop={dropGuest}
                      onRemove={removeGuest}
                      onUpdate={updateTable}
                      onDuplicate={duplicateTable}
                      onDelete={removeTable}
                      onDragStart={handleTableDragStart}
                      isActive={dragGuestIdx !== null}
                      isSelected={selectedTableId === table.id}
                      onSelect={(id) => { setSelectedTableId(id); setSelectedVenueId(null); }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ─── Sidebar ─── */}
        <div className="w-60 flex-shrink-0 space-y-3 overflow-y-auto" style={{ maxHeight: 600 }} data-testid="seating-sidebar">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 w-3 h-3 text-stone-400" />
            <input data-testid="seating-search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search guests..."
              className="w-full text-[11px] font-sans pl-7 pr-3 py-2 border border-stone-200 rounded-xl bg-white focus:border-primary focus:outline-none" />
          </div>

          {/* Tag filters */}
          <div className="flex flex-wrap gap-1">
            {Object.entries(RELATION_LABELS).map(([key, label]) => (
              <button key={key} onClick={() => setFilterTag(filterTag === key ? null : key)}
                className={`text-[9px] font-sans font-semibold px-2 py-1 rounded-full border transition-all ${
                  filterTag === key ? RELATION_COLORS[key] + ' ring-1 ring-primary/30' : 'bg-white border-stone-200 text-stone-500 hover:border-stone-300'
                }`}>
                {label}
              </button>
            ))}
            {filterTag && (
              <button onClick={() => setFilterTag(null)} className="text-[9px] font-sans text-stone-400 hover:text-stone-600 px-1">
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Auto-assign */}
          {unassigned.length > 0 && (
            <button data-testid="auto-assign-btn" onClick={autoAssign}
              className="w-full text-[10px] font-sans font-semibold text-primary bg-primary/5 hover:bg-primary/10 border border-primary/20 rounded-xl py-1.5 transition-colors">
              Automatisk seatsing ({unassigned.length})
            </button>
          )}

          {/* Guest list */}
          <div className="space-y-1 pr-0.5">
            {filteredUnassigned.length === 0 ? (
              <p className="text-[10px] font-sans text-stone-400 text-center py-6">
                {validGuests.length === 0 ? 'Add guester i the guest list first' :
                  unassigned.length === 0 ? 'All guests are seated!' :
                  'No results found'}
              </p>
            ) : (
              filteredUnassigned.map(g => (
                <GuestChip key={g._idx} guest={g} index={g._idx}
                  isDragging={dragGuestIdx === g._idx}
                  onDragStart={setDragGuestIdx} onDragEnd={() => setDragGuestIdx(null)} />
              ))
            )}
          </div>

          {/* Warnings */}
          <WarningsPanel warnings={warnings} />
        </div>
      </div>
    </div>
  );
};
